import React, {
  useState,
  ReactNodeArray,
  ReactNode,
  useMemo,
  useEffect,
  CSSProperties,
} from 'react'
import { Button, Icon, PrettyNumber } from '.'
import { IDHash, Order } from '@api/types'
import { useIntersect } from '@hooks'
import { times } from 'lodash'
import { sum } from 'd3-array'
import { ISelection } from '@hooks/useSelection'
import { Checkbox, Intent, ProgressBar } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import classnames from 'classnames'

export type GridTableStatus = 'idle' | 'loading' | 'paging' | 'sorting' | 'filtering'

export enum GridTableStatusEnum {
  IDLE = 'idle',
  LOADING = 'loading',
  PAGING = 'paging',
  SORTING = 'sorting',
  FILTERING = 'filtering',
}

export interface IGridTableCell<T> {
  data: T
  // Content of expand/collapse toggle
  expandToggle?: ReactNode
  // Child component to be rendered on row expand
  child?: ReactNode
  // Nested data - can be populated with onExpand callback
  children?: IGridTableCell<T>[]
  // Page for nested data
  page?: IGridTablePage
  // Status for nested data
  status?: GridTableStatus
  // Number of rows in skeleton
  skeletonSize?: number
  disabled?: boolean
  // Async callback function called on row expand
  // Used to request nested data
  onExpand?: (d: IGridTableCell<T>) => void
  expanded?: boolean
  onExpandCallback?: (d: IGridTableCell<T>) => void
}

export interface IGridTableCellProps<T> {
  // GridTable will renders <Cell skeleton /> when status === 'loading'
  skeleton?: boolean
  cell?: IGridTableCell<T>
  isHighlighted?: boolean
}

export interface IGridTableColumn<T> {
  label: ReactNode
  // Sort attribute to be send to onSort callback
  sort?: string
  // CSS grid column span
  colSpan?: number
  // content alignment - used for number
  alignRight?: boolean
  // Component that will be render in each cell for this column
  Cell: React.FC<React.PropsWithChildren<IGridTableCellProps<T>>>
  tooltip?: JSX.Element
  // Apply white-space: nowrap for header
  noWrapHeader?: boolean
  // custom styles for header
  headerStyle?: CSSProperties
  // custom styles for cell
  cellStyle?: CSSProperties
}

export interface IGridTablePage {
  hasNextPage: boolean
  endCursor: string
  onNextPage: () => void
}

export interface IGridTableSort {
  column: string
  order: Order
  onSort: (column: string) => void
}

export interface IGridTableProps<T, K> {
  status: GridTableStatus
  columns: IGridTableColumn<T>[]
  data: IGridTableCell<T>[]
  page?: IGridTablePage
  sort?: IGridTableSort
  selection?: ISelection
  noDataMessage?: ReactNode
  // Number of rows in skeleton
  skeletonSize?: number
  // Show/hide header row (true by default)
  header?: boolean
  // Sticky header (true by default)
  sticky?: boolean
  // Use infinite scroll (true by default)
  infiniteScroll?: boolean
  // Talbe row click handler
  onRowClick?: (d: IGridTableCell<T>) => void
  track?: { select?: () => void; selectAll?: () => void }
  //embedded table or not
  childTable?: boolean
  //pass value to min to minmax function (feel free to add necessary values when needed as enum)
  minCellWidth?: '0' | 'auto' | 'max-content'
  //pass value to max to minmax function (feel free to add necessary values when needed as enum)
  maxCellWidth?: '1fr' | 'auto'
  // array for grid-template-column function with custom widths
  customColumnWidths?: string[]
  // total row can use different type of data
  appendData?: IGridTableCell<K>[]
  appendColumns?: IGridTableColumn<K>[]
  headerTop?: number
}

const CELL_PL = 10

export function NewGridTable<T extends { id: string; disabled?: boolean }, K = undefined>({
  status,
  columns,
  data,
  page,
  sort,
  selection,
  noDataMessage,
  skeletonSize,
  header = true,
  sticky = true,
  infiniteScroll = true,
  minCellWidth = '0',
  maxCellWidth = '1fr',
  customColumnWidths,
  onRowClick,
  track,
  childTable,
  appendData,
  appendColumns,
  headerTop = 0,
}: IGridTableProps<T, K>) {
  const [expanded, setExpanded] = useState<IDHash<boolean>>({})

  useEffect(() => {
    const filteredExpandObj = data.reduce((acc, i) => {
      if (i.expanded && expanded[i.data.id] === undefined) {
        i.onExpandCallback?.(i)
        return { ...acc, [i.data.id]: true }
      }
      return acc
    }, {})
    Object.keys(filteredExpandObj).length && setExpanded({ ...expanded, ...filteredExpandObj })
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [data])

  const [highlighted, setHighlighted] = useState<null | string>(null)
  let cells: ReactNodeArray = []
  let appendCells: ReactNodeArray = []

  const renderSkeleton = status !== 'idle' || (page?.hasNextPage && infiniteScroll)
  const renderPager = status === 'idle' && page?.hasNextPage
  const allRowsDisabled = data.every(({ data }) => data.disabled)
  const totalColSpan = getColSpan(columns)

  // Reset expand/collapse state on sort|filter
  // and clear selection
  useEffect(() => {
    if (status === 'sorting' || status === 'filtering') {
      if (selection?.count) selection?.clear()
    }
  }, [status, selection])

  // A flag to indicat if all items in the table are selected
  const allSelected = useMemo(() => {
    const selectedIds = selection?.items || {}
    function selected(items: IGridTableCell<T>[]): boolean {
      return items.every(({ data, children }) => {
        const allChildrenSelected: boolean = !children || selected(children)
        return (selectedIds[data.id] || data.disabled) && allChildrenSelected
      })
    }
    return data.length > 0 && selected(data)
  }, [selection, data])

  // List of all ids in the data tree
  const allIds = useMemo(() => getIds(data), [data])

  // Render table cells recursively
  if (data.length && (status === 'idle' || status === 'paging')) {
    cells = data.reduce((result, d, i) => {
      return [
        ...result,
        ...renderCells({
          columns,
          selection,
          d,
          lastRow: i === data.length - 1,
          highlighted,
          setHighlighted,
          expanded,
          toggleExpanded: (id) =>
            setExpanded({
              ...expanded,
              [id]: !expanded[id],
            }),
          onRowClick,
          track,
          childTable,
        }),
      ]
    }, cells)

    appendCells =
      appendData && appendColumns
        ? appendData.reduce((result, d, i) => {
            return [
              ...result,
              ...renderAppend({
                columns: appendColumns,
                d,
              }),
            ]
          }, appendCells)
        : []
  }

  return (
    <div
      className="grid"
      style={{
        borderBottomColor: 'var(--border-color)',
        gridTemplateColumns: selection
          ? `32px ${
              customColumnWidths
                ? customColumnWidths.join(' ')
                : `repeat(${totalColSpan}, minmax(${minCellWidth}, ${maxCellWidth}))`
            }`
          : customColumnWidths
          ? customColumnWidths.join(' ')
          : `repeat(${totalColSpan}, minmax(${minCellWidth}, ${maxCellWidth}))`,

        borderBottomWidth: childTable ? 1 : 0,

        // borderBottomWidth: 1, //todo finish boxshadow for child table
        borderBottomStyle: 'solid',
        boxShadow: childTable ? '0px 4px 2px -2px var(--grey8)' : undefined,
      }}
    >
      {/* Render checkbox table header */}
      {header && selection && (
        <THead
          key="checkbox"
          checkbox
          sticky={sticky}
          column={{
            label: (
              <Checkbox
                disabled={allRowsDisabled}
                checked={allSelected && !allRowsDisabled}
                aria-label="select_all"
                onChange={() => {
                  if (status !== 'idle' || allRowsDisabled) return

                  if (allSelected) {
                    return selection.clear()
                  }
                  if (track?.selectAll) track?.selectAll()
                  selection.addAll(allIds)
                }}
              />
            ),
            Cell: () => null,
          }}
          headerTop={headerTop}
        />
      )}
      {/* Render table headers */}
      {header &&
        columns.map((column, i) => (
          <THead key={i} sticky={sticky} column={column} sort={sort} headerTop={headerTop} />
        ))}
      {cells}
      {appendCells}
      {!data.length && status === 'idle' && (
        <NoData colSpan={totalColSpan} noDataMessage={noDataMessage} />
      )}
      {renderPager && (
        <Pager
          level={0}
          infiniteScroll={infiniteScroll}
          colSpan={totalColSpan}
          page={page as IGridTablePage}
          hasSelection={!!selection}
        />
      )}
      {renderSkeleton && (
        <Skeleton
          {...{
            columns,
            skeletonSize,
            level: 0,
            hasSelection: !!selection,
          }}
        />
      )}
    </div>
  )
}

function Skeleton<T>({
  hasSelection,
  skeletonSize,
  columns,
  level,
}: {
  hasSelection?: boolean
  columns: IGridTableColumn<T>[]
  skeletonSize?: number
  level: number
}) {
  return (
    <>
      {times(skeletonSize || 5, Number).reduce((cells: ReactNodeArray, _, r) => {
        let row = columns.map(({ colSpan, alignRight, label, Cell, cellStyle }, c) => (
          <div
            key={`${r}-${c}-${label}`}
            {...getCellStyles({
              level,
              colSpan,
              alignRight,
              firstCell: c === 0,
              cellStyle,
            })}
          >
            {c === 0 && <ExpandIndicators level={level} hasSelection={hasSelection} />}
            <Cell skeleton />
          </div>
        ))

        if (hasSelection) {
          row = [<div key={`checkbox-${r}`} {...getCheckboxCellStyles({})} />, ...row]
        }

        return [...cells, ...row]
      }, [])}
    </>
  )
}

function Pager({
  page,
  colSpan,
  level,
  infiniteScroll,
  hasSelection,
}: {
  page: IGridTablePage
  level: number
  colSpan: number
  infiniteScroll: boolean
  hasSelection?: boolean
}) {
  const { refItem } = useIntersect({
    callback: page.onNextPage,
  })
  return infiniteScroll ? (
    <div
      ref={refItem as React.Ref<HTMLDivElement>}
      className={`col-span-${hasSelection ? colSpan + 1 : colSpan}`}
    ></div>
  ) : (
    <>
      {hasSelection && <div className="border-grey-200 border-t" />}
      <div
        className={`relative border-grey-200 border-t p-3 col-span-${colSpan}`}
        style={{ paddingLeft: (level + 1) * CELL_PL }}
      >
        <ExpandIndicators level={level} hasSelection={hasSelection} />
        <Button minimal small onClick={page.onNextPage} text="Load more" />
      </div>
    </>
  )
}

function NoData({ colSpan, noDataMessage }: { colSpan: number; noDataMessage?: ReactNode }) {
  return (
    <div className={`col-span-${colSpan} py-10 items-center text-center`}>
      <Icon name="search" className="mr-2" />
      {noDataMessage || 'No data...'}
    </div>
  )
}

function THead<T>({
  column,
  sort,
  checkbox,
  sticky,
  headerTop,
}: {
  column: IGridTableColumn<T>
  sort?: IGridTableSort
  checkbox?: boolean
  sticky: boolean
  headerTop: number
}) {
  let tHead

  if (checkbox) {
    return (
      <div
        className={classnames(
          'border-b border-grey-200 bg-white',
          {
            sticky: sticky,
            'z-10': sticky,
          },
          { nowrap: column.noWrapHeader }
        )}
        style={{ top: sticky ? headerTop : 'auto', ...column.headerStyle }}
      >
        <div className="flex items-center h-full pl-4">{column.label}</div>
      </div>
    )
  }

  if (sort && column.sort) {
    tHead = (
      <div
        className={classnames('inline-flex items-center', {
          nowrap: column.noWrapHeader,
        })}
        style={{ ...column.headerStyle }}
      >
        {column.label}
        <Icon
          name={`long-arrow-${sort.order === 'ASC' ? 'up' : 'down'}`}
          className={classnames('ml-4', {
            block: sort.column === column.sort,
            hidden: sort.column !== column.sort,
          })}
        />
      </div>
    )
  }

  return (
    <div
      onClick={() => sort && column.sort && sort.onSort(column.sort as string)}
      {...getTHeadStyles({
        active: sort?.column === column.sort,
        sticky,
        column,
        headerTop,
      })}
    >
      {column.tooltip && (column.label || tHead) ? (
        <Tooltip2 content={column.tooltip}>{tHead || column.label}</Tooltip2>
      ) : (
        tHead || column.label
      )}
    </div>
  )
}

function renderCells<T extends { id: string; disabled?: boolean }>({
  columns,
  selection,
  d,
  level = 0,
  lastRow,
  highlighted,
  setHighlighted,
  expanded,
  toggleExpanded,
  onRowClick,
  track,
  childTable,
}: {
  columns: IGridTableColumn<T>[]
  selection?: ISelection
  d: IGridTableCell<T>
  level?: number
  lastRow?: boolean
  highlighted: null | string
  expanded: IDHash<boolean>
  setHighlighted: (id: null | string) => void
  toggleExpanded: (id: string) => void
  onRowClick?: (d: IGridTableCell<T>) => void
  track?: { select?: () => void; selectAll?: () => void }
  childTable?: boolean
}) {
  const isExpanded = expanded[d.data.id]
  const isHighlighted = highlighted === d.data.id
  const isSelected = selection?.items[d.data.id]

  // Render table row
  let cells = columns.map(({ colSpan, alignRight, label, Cell, cellStyle }, i) => {
    return (
      <div
        key={`${d.data.id}-${label}-${i}`}
        onClick={() => {
          if (!window.getSelection?.()?.toString()) {
            onRowClick?.(d)
          }
        }}
        onMouseEnter={() => setHighlighted(d.data.id)}
        onMouseLeave={() => setHighlighted(null)}
        {...getCellStyles({
          level,
          colSpan,
          alignRight,
          lastRow: lastRow && !isExpanded,
          firstCell: i === 0,
          clickable: !!onRowClick,
          isHighlighted,
          isSelected,
          cellStyle,
        })}
      >
        {/* Render dotted lines only for the first cell */}
        {i === 0 && <ExpandIndicators level={level} hasSelection={!!selection} />}
        <Cell cell={d} isHighlighted={isHighlighted} />
        {/* Render expand/collapse toggle */}
        {i === 0 && (d.child || d.children?.length || d.onExpand) && (
          <div className="z-1 mt-1">
            <TableButton
              active={isExpanded}
              onClick={() => {
                if (isExpanded) {
                  // Remove children from selection
                  // if (d.children) selection?.remove(getIds(d.children))
                  d.onExpand?.(d)
                } else {
                  d.onExpand?.(d)
                  d.onExpandCallback?.(d)
                }
                toggleExpanded(d.data.id)
              }}
            >
              <div className="flex">
                <div className="flex items-center content-start w-4">
                  <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} className="mr-2" />
                </div>
                <div className="font-medium">{d.expandToggle || 'Expand'}</div>
              </div>
            </TableButton>
          </div>
        )}
      </div>
    )
  })

  // Add checkbox cell
  if (selection) {
    cells = [
      <div
        key={`checkbox-${d.data.id}`}
        onMouseEnter={() => setHighlighted(d.data.id)}
        onMouseLeave={() => setHighlighted(null)}
        {...getCheckboxCellStyles({
          lastRow: lastRow && !isExpanded,
          isHighlighted,
          isSelected,
        })}
      >
        <Tooltip2
          content={d.data.disabled ? 'You do not have permission to edit this booking.' : ''}
        >
          <Checkbox
            disabled={d.data.disabled}
            checked={d.data.disabled ? false : selection.items[d.data.id] || false}
            onChange={() => {
              if (!d.data.disabled) {
                selection.toggle(d.data.id)
                if (!selection.items[d.data.id] && track?.select) {
                  track.select()
                }
              }
            }}
          />
        </Tooltip2>
      </div>,
      ...cells,
    ]
  }

  // Render children recursively
  if (isExpanded) {
    if (d.children) {
      d.children.forEach((d, i) => {
        cells = [
          ...cells,
          ...renderCells({
            columns,
            selection,
            d,
            level: level + 1,
            highlighted,
            setHighlighted,
            expanded,
            toggleExpanded,
            onRowClick,
            lastRow: d.children ? i === d.children?.length - 1 : undefined,
            childTable,
          }),
        ]
      })

      // Add Skeleton for children
      if (d.status === 'paging') {
        cells = [
          ...cells,
          <Skeleton
            key="skele"
            hasSelection={!!selection}
            skeletonSize={d.skeletonSize}
            level={level + 1}
            columns={columns}
          />,
        ]
      }

      // Add pager for children
      if (d.page?.hasNextPage && d.status !== 'paging') {
        cells = [
          ...cells,
          <Pager
            key="pager"
            level={level + 1}
            infiniteScroll={false}
            colSpan={getColSpan(columns)}
            page={d.page}
            hasSelection={!!selection}
          />,
        ]
      }
    }

    if (d.status === 'loading' && !d.children?.length) {
      cells = [
        ...cells,
        <Skeleton
          key="skelet"
          hasSelection={!!selection}
          skeletonSize={d.skeletonSize}
          level={level + 1}
          columns={columns}
        />,
      ]
    }
  }

  // Add a cell spanning full width and render child node
  if (isExpanded && d.child) {
    const colSpan = columns.reduce((sum, { colSpan }) => sum + (colSpan || 1), 0)
    cells = [
      ...cells,
      <div
        key={d.data.id}
        onMouseEnter={() => setHighlighted(d.data.id)}
        onMouseLeave={() => setHighlighted(null)}
        className={`relative border-grey-200 border-t col-span-${colSpan}`}
        style={{ paddingLeft: (level + 2) * CELL_PL }}
      >
        {!childTable && <ExpandIndicators level={level + 1} hasSelection={!!selection} />}
        <div className="self-stretch">{d.child}</div>
      </div>,
    ]
  }

  return cells
}

function renderAppend<K>({ columns, d }: { columns: IGridTableColumn<K>[]; d: IGridTableCell<K> }) {
  const cells = columns.map(({ colSpan, alignRight, label, Cell, cellStyle }, i) => {
    return (
      <div
        key={`${label}-${i}`}
        {...getAppendCellStyles({
          level: 0,
          colSpan,
          alignRight,
          cellStyle,
        })}
      >
        {/* Render dotted lines only for the first cell */}
        <Cell cell={d} />
      </div>
    )
  })

  return cells
}

function ExpandIndicators({ level, hasSelection }: { level: number; hasSelection?: boolean }) {
  const indicators = times(level >= 1 ? 1 : 0, Number).map((n) => (
    <div
      key={n}
      className="absolute border-l-2 border-blue-400"
      style={{
        top: -1,
        bottom: -1,
        left: hasSelection ? '-32px' : (n + 1) * CELL_PL + 8,
      }}
    />
  ))

  return <>{indicators}</>
}

function TableButton({
  children,
  onClick,
  active,
}: {
  children: ReactNode
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      data-test="table_button"
      style={{ outline: 'none' }}
      className={classnames(
        'p-1 font-medium text-xs',
        { 'text-blue-400': active },
        { 'text-grey-600 hover:text-grey-700': !active }
      )}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function getColSpan<T>(columns: IGridTableColumn<T>[]) {
  return sum(columns, ({ colSpan }) => colSpan || 1)
}

// Helper functions to get class names and styles
function getTHeadStyles<T>({
  sticky,
  active,
  column,
  headerTop,
}: {
  sticky: boolean
  active: boolean
  column: IGridTableColumn<T>
  headerTop: number
}) {
  const style = {
    paddingLeft: CELL_PL,
    top: sticky ? headerTop : 'auto',
    cursor: column.sort ? 'pointer' : 'default',
    ...column.headerStyle,
  }

  const className = classnames(
    `py-2 pr-4 border-b border-grey-200 bg-white text-xs capitalize col-span-${
      column.colSpan || 1
    }`,
    { 'text-grey-900': active },
    { 'text-grey-600': !active },
    { 'hover:text-grey-900': column.sort },
    { 'font-semibold': column.sort },
    { 'text-right': column.alignRight },
    { 'sticky z-10': sticky },
    { nowrap: column.noWrapHeader }
  )

  return { className, style }
}
function getCellStyles({
  level,
  firstCell,
  lastRow,
  colSpan,
  alignRight,
  clickable,
  isSelected,
  isHighlighted,
  cellStyle,
}: {
  level: number
  firstCell?: boolean
  lastRow?: boolean
  colSpan?: number
  alignRight?: boolean
  clickable?: boolean
  isSelected?: boolean
  isHighlighted?: boolean
  cellStyle?: CSSProperties
}) {
  const className = classnames(
    'relative',
    'flex',
    'flex-col',
    'items-start',
    'justify-center',
    'py-3',
    'pr-4',
    'border-grey-200',
    'border-b',
    `col-span-${colSpan || 1}`,
    { 'items-end': alignRight },
    { 'border-none': lastRow },
    { 'bg-blue-100': isSelected }
  )

  const style = {
    paddingLeft: firstCell ? (level + 1) * CELL_PL : CELL_PL,
    cursor: clickable ? 'pointer' : 'default',
    backgroundColor: isHighlighted ? '#eee' : undefined,
    ...cellStyle,
  }

  return { className, style }
}

function getAppendCellStyles({
  level = 0,
  firstCell,
  colSpan,
  alignRight,
  cellStyle,
}: {
  level?: number
  firstCell?: boolean
  colSpan?: number
  alignRight?: boolean
  cellStyle?: CSSProperties
}) {
  const className = classnames(
    'relative',
    'flex',
    'flex-col',
    'items-start',
    'justify-center',
    'py-3',
    'pr-4',
    `col-span-${colSpan || 1}`,
    { 'items-end': alignRight }
  )

  const style = {
    paddingLeft: firstCell ? (level + 1) * CELL_PL : CELL_PL,
    ...cellStyle,
  }

  return { className, style }
}

function getCheckboxCellStyles({
  lastRow,
  isHighlighted,
  isSelected,
  isDisabled,
}: {
  lastRow?: boolean
  isSelected?: boolean
  isHighlighted?: boolean
  isDisabled?: boolean
}) {
  const className = classnames(
    'relative',
    'flex',
    'flex-col',
    'items-start',
    'pl-4',
    'justify-center',
    'border-grey-200',
    'border-b',
    { 'bg-white': !isSelected },
    { 'border-none': lastRow },
    { 'bg-blue-100': isSelected }
  )

  return {
    className,
    style: {
      backgroundColor: isHighlighted ? '#eee' : undefined,
    },
  }
}

function getIds<T extends { id: string; disabled?: boolean }>(
  items: IGridTableCell<T>[]
): string[] {
  return items.reduce((allIds, { data: { id, disabled }, children }) => {
    const allChildrenIds = children ? getIds(children) : []
    return disabled ? allIds : [...allIds, id, ...allChildrenIds]
  }, [] as string[])
}

NewGridTable.Number = function ({ value }: { value: string | number }) {
  return <PrettyNumber value={value} />
}

NewGridTable.PercentBar = function ({
  value,
  normal,
  alert,
}: {
  value: number
  normal?: number
  alert?: number
}) {
  let intent: Intent | undefined
  if (normal && value >= normal) intent = Intent.WARNING
  if (alert && value >= alert) intent = Intent.DANGER
  return (
    <div className="f f10">
      <div className="f10 mr2 mt1">
        <ProgressBar animate={false} intent={intent} stripes={false} value={value / 100} />
      </div>
      {`${value}%`}
    </div>
  )
}
